/**
 * Client-side access to the data layer.
 *
 * Under Next these reads happened inside async server components. Here they
 * happen in route loaders, which React Router runs before the component
 * renders — so the shape of a page is unchanged: get the data, then render it,
 * never a component that renders empty and fills in later.
 *
 * `read()` batches, so a page that needed three tables still costs one request.
 */

import type {
  AuditLogRow,
  Brand,
  CommissionLedgerRow,
  Order,
  PartnershipConfig,
  PrintMethodRow,
  Product,
  PublishedProduct,
  PushCampaign,
  Quote,
  ReadyDesign,
  SiteConfig,
  Tenant,
} from './types';
import type { CatalogueItem } from './data';

export interface ApiErrorBody {
  error: { code: string; message: string; details: Record<string, unknown> };
}

export class ApiError extends Error {
  code: string;
  status: number;
  details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

/**
 * What to say when the answer is not the API's own error shape.
 *
 * The server answers every refusal with `{ error: { code, message } }`, and
 * that message is written for a person. This is the other case: a bare status
 * from something in between, a proxy, a crash before any body was written.
 *
 * It used to be `res.statusText`, so the screen a customer read said "Internal
 * Server Error". That names the layer that failed and tells them nothing to do
 * about it. R-184.
 */
export function transportFailure(status: number): ApiErrorBody['error'] {
  return {
    code: 'UNEXPECTED',
    message: 'We could not reach the system just now. Try again in a moment.',
    details: { status },
  };
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const parsed = (await res.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      res.status,
      parsed?.error ?? transportFailure(res.status),
    );
  }
  return (await res.json()) as T;
}

interface ReadCall {
  fn: string;
  args?: unknown[];
}

/** Run up to 12 allowlisted reads in one round trip. */
export async function read<T extends unknown[]>(...calls: ReadCall[]): Promise<T> {
  const { results } = await post<{ results: T }>('/api/data', { calls });
  return results;
}

/* Convenience wrappers, one per read, so loaders read like the old server code. */

export const q = {
  brands: (): ReadCall => ({ fn: 'getBrands' }),
  brand: (slug: string): ReadCall => ({ fn: 'getBrand', args: [slug] }),
  products: (): ReadCall => ({ fn: 'getProducts' }),
  product: (id: string): ReadCall => ({ fn: 'getProduct', args: [id] }),
  printMethods: (): ReadCall => ({ fn: 'getPrintMethods' }),
  /**
   * Every method, switched on or off, for the screen whose job is to switch
   * them on. `printMethods` filters to active and is right for the storefront;
   * using it on the admin screen created a deadlock where the only methods
   * that could be edited were the ones that did not need editing. R-238.
   */
  allPrintMethods: (): ReadCall => ({ fn: 'getAllPrintMethods' }),
  printMethod: (key: string): ReadCall => ({ fn: 'getPrintMethod', args: [key] }),
  readyDesigns: (): ReadCall => ({ fn: 'getReadyDesigns' }),
  publishedProducts: (brand?: string): ReadCall => ({
    fn: 'getPublishedProducts',
    args: brand ? [brand] : [],
  }),
  catalogue: (brand?: string): ReadCall => ({
    fn: 'getCatalogue',
    args: brand ? [brand] : [],
  }),
  catalogueItem: (id: string): ReadCall => ({ fn: 'getCatalogueItem', args: [id] }),
  siteConfig: (): ReadCall => ({ fn: 'getSiteConfig' }),
  tenant: (): ReadCall => ({ fn: 'getTenant' }),
  orders: (): ReadCall => ({ fn: 'getOrders' }),
  allOrders: (): ReadCall => ({ fn: 'getAllOrders' }),
  orderByRef: (ref: string, token?: string): ReadCall =>
    ({ fn: 'getOrderByRef', args: token ? [ref, token] : [ref] }),
  quotes: (): ReadCall => ({ fn: 'getQuotes' }),
  quotesForUser: (): ReadCall => ({ fn: 'getQuotesForUser' }),
  quoteByRef: (ref: string, token?: string): ReadCall =>
    ({ fn: 'getQuoteByRef', args: token ? [ref, token] : [ref] }),
  auditLog: (): ReadCall => ({ fn: 'getAuditLog' }),
  jobs: (): ReadCall => ({ fn: 'getJobs' }),
  jobByRef: (ref: string): ReadCall => ({ fn: 'getJobByRef', args: [ref] }),
  machines: (includeRetired?: boolean): ReadCall =>
    ({ fn: 'getMachines', args: includeRetired ? [true] : [] }),
};

/* -------------------------------------------------------------------------- */
/* Session                                                                     */
/* -------------------------------------------------------------------------- */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'customer' | 'staff' | 'admin';
}

export interface Me {
  user: SessionUser | null;
  isAdmin: boolean;
  isInternal: boolean;
  /** Server facts the UI branches on, so the browser never reads env. */
  stripeConfigured: boolean;
  supabaseConfigured: boolean;
}

const NO_SESSION: Me = {
  user: null,
  isAdmin: false,
  isInternal: false,
  stripeConfigured: false,
  supabaseConfigured: false,
};

export async function getMe(): Promise<Me> {
  const res = await fetch('/api/me', { credentials: 'same-origin' });
  if (!res.ok) return NO_SESSION;
  return (await res.json()) as Me;
}

export async function signIn(email: string): Promise<{ email: string; role: string }> {
  return post('/api/session', { email });
}

export async function signOut(): Promise<void> {
  await fetch('/api/session', { method: 'DELETE', credentials: 'same-origin' });
}

/* -------------------------------------------------------------------------- */
/* Trusted write paths                                                         */
/* -------------------------------------------------------------------------- */

export const callEstimate = <T>(body: unknown) => post<T>('/api/estimate', body);
export const callCartPrice = <T>(body: unknown) => post<T>('/api/cart-price', body);
export const callCheckout = <T>(body: unknown) => post<T>('/api/checkout', body);
export const callQuotesSubmit = <T>(body: unknown) => post<T>('/api/quotes-submit', body);
export const callProofsRespond = <T>(body: unknown) => post<T>('/api/proofs-respond', body);
export const callUploadsSign = <T>(body: unknown) => post<T>('/api/uploads-sign', body);

export type {
  AuditLogRow,
  Brand,
  CatalogueItem,
  CommissionLedgerRow,
  Order,
  PartnershipConfig,
  PrintMethodRow,
  Product,
  PublishedProduct,
  PushCampaign,
  Quote,
  ReadyDesign,
  SiteConfig,
  Tenant,
};
